import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet";
import DriverPage from "./DriverPage";
import { useAuth } from "../../context/AuthContext";
import { PulseLoader } from 'react-spinners';
import { FaRoad, FaMapMarkerAlt, FaMapPin, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import {
    fetchAvailableTrips,
    acceptTrip,
    rejectTrip
} from "../../Services/ApiCallsServices/DriverDashboardApi";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useDispatch } from "react-redux";
import { setTripReference, setTripInfo } from "../../Reducers/tripSlice";

const TripDetails = () => {
    const dispatch = useDispatch();
    const [trips, setTrips] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isActionInProgress, setIsActionInProgress] = useState(false);
    const navigate = useNavigate();
    const { auth } = useAuth();
    const email = auth.user?.email;

    useEffect(() => {
        if (!auth.isAuthenticated) return;

        const loadTrips = async () => {
            try {
                const data = await fetchAvailableTrips();

                if (data.trips && Array.isArray(data.trips)) {
                    setTrips(data.trips);
                } else {
                    setError(data.error || "No trips available.");
                }
            } catch (err) {
                setError("Failed to load trips. Please try later.");
            } finally {
                setIsLoading(false);
            }
        };

        loadTrips();
    }, [auth.isAuthenticated]);


    const handleAccept = async (reference) => {
        if (!email || !reference) return;
        setIsActionInProgress(true);

        // Optimistic UI update
        const remainingTrips = trips.filter(trip => trip.reference !== reference);
        setTrips(remainingTrips);

        try {
            const data = await acceptTrip(email, reference);

            if (data.tripInfo) {
                toast.success("Trip accepted!");
                dispatch(setTripReference(reference));
                dispatch(setTripInfo(data.tripInfo));
                navigate("/trip-progress");
            } else {
                throw new Error(data.error || "Acceptance failed");
            }
        } catch (err) {
            setTrips([...remainingTrips, ...trips.filter(t => t.reference === reference)]);
            toast.error(err.message);
        } finally {
            setIsActionInProgress(false);
        }
    };

    // Handle trip rejection
    const handleRejectTrip = async (reference) => {
        if (!email || !reference) return;

        const remainingTrips = trips.filter(trip => trip.reference !== reference);
        setTrips(remainingTrips);

        try {
            await rejectTrip(email, reference);
            toast.success("Trip rejected");
        } catch (err) {
            setTrips([...remainingTrips, ...trips.filter(t => t.reference === reference)]);
            toast.error("Rejection failed. Try again.");
        }
    };

    // Main render
     return (
        <DriverPage>
            <Helmet>
                <title>Available Trips | Storage Sprint</title>
            </Helmet>
            
            <div className="min-h-screen bg-gray-50 p-4 md:p-8">
                {/* Loading State */}
                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <PulseLoader color="#FFD700" size={15} />
                    </div>
                ) : error ? (
                    /* Error State */
                    <div className="bg-white rounded-xl shadow-sm p-6 text-center">
                        <div className="text-red-500 text-4xl mb-4">⚠️</div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Trips</h3>
                        <p className="text-gray-600">{error}</p>
                    </div>
                ) : trips.length === 0 ? (
                    /* Empty State */
                    <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                        <div className="text-yellow-500 text-4xl mb-4">🚛</div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">No Available Trips</h3>
                        <p className="text-gray-500">We'll notify you when new trips become available</p>
                    </div>
                ) : (
                    /* Main Content */
                    <div className="max-w-6xl mx-auto">
                        <div className="mb-8 text-center">
                            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
                                <span className="text-yellow-500">Available</span> Trips
                            </h1>
                            <p className="text-gray-600">
                                Browse through our available trips below and choose the one that suits you best
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {trips.map((trip) => (
                                <div key={trip.reference} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow">
                                    <div className="p-6">
                                        {/* Trip Header */}
                                        <div className="flex justify-between items-start mb-4">
                                            <h3 className="font-bold text-lg text-gray-800">Trip #{trip.reference}</h3>
                                            <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                                                R{trip.price}
                                            </span>
                                        </div>

                                        {/* Trip Details */}
                                        <div className="space-y-3 mb-6">
                                            <div className="flex items-center">
                                                <FaRoad className="text-yellow-500 mr-3" />
                                                <span className="text-gray-700">{trip.distance} km</span>
                                            </div>
                                            <div className="flex items-start">
                                                <FaMapMarkerAlt className="text-yellow-500 mr-3 mt-1" />
                                                <span className="text-gray-700">{trip.pickupLocation}</span>
                                            </div>
                                            <div className="flex items-start">
                                                <FaMapPin className="text-yellow-500 mr-3 mt-1" />
                                                <span className="text-gray-700">{trip.dropoffLocation}</span>
                                            </div>
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex space-x-3">
                                            <button
                                                onClick={() => handleAccept(trip.reference)}
                                                disabled={isActionInProgress}
                                                className={`flex-1 flex items-center justify-center py-2 px-4 bg-yellow-500 hover:bg-yellow-600 text-white font-medium rounded-lg transition-colors ${
                                                    isActionInProgress ? 'opacity-70 cursor-not-allowed' : ''
                                                }`}
                                            >
                                                {isActionInProgress ? (
                                                    <PulseLoader color="#ffffff" size={8} />
                                                ) : (
                                                    <>
                                                        <FaCheckCircle className="mr-2" />
                                                        Accept
                                                    </>
                                                )}
                                            </button>
                                            <button
                                                onClick={() => handleRejectTrip(trip.reference)}
                                                className="flex-1 flex items-center justify-center py-2 px-4 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded-lg transition-colors"
                                            >
                                                <FaTimesCircle className="mr-2" />
                                                Decline
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <ToastContainer position="bottom-right" autoClose={3000} />
        </DriverPage>
    );
};

export default TripDetails;